import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, CheckCircle2, ChevronRight, CloudDownload, Database, HardDrive, Layers, Loader2, Package, Pause, RefreshCw, Settings2, ShieldCheck, Smartphone, Trash2, UserRound, Users, WalletCards, WifiOff } from 'lucide-react';
import { useOffline } from '../contexts/OfflineContext';
import { useInteractionPreferences } from '../contexts/InteractionPreferencesContext';
import { OFFLINE_DAY_OPTIONS } from '../utils/offlineSync';
import { formatOfflineCacheDateTime } from '../utils/offlineRoutesCache';
import { haptic } from '../utils/vibration';
import useModalBackButton from '../hooks/useModalBackButton';
import ModalShell from './ModalShell';
import WindowModalHeader from './WindowModalHeader';

const formatBytes = (bytes) => bytes >= 1024 * 1024
  ? `${(bytes / (1024 * 1024)).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} МБ`
  : `${Math.max(0, Math.round(bytes / 1024)).toLocaleString('ru-RU')} КБ`;
const PROGRESS_RESULT_MIN_MS = 1800;
const dayLabel = (days) => ({ 1: 'Сегодня', 3: '3 дня', 7: '7 дней', 14: '14 дней' }[days]);
const primaryButton = 'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40';
const secondaryButton = 'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-40 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800';
const tones = {
  blue: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
  red: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  amber: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
  green: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/35 dark:text-emerald-300',
  gray: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
};

export const TelegramDownloadIcon = ({ downloading = false, completed = false, animated = true, className = 'h-[18px] w-[18px]' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 16v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2" />
    {completed ? (
      <polyline points="8 11.5 11 14.5 16.5 9" />
    ) : downloading ? (
      <g className={animated ? 'animate-telegram-arrow' : ''}>
        <line x1="12" y1="3" x2="12" y2="13.5" />
        <polyline points="7.5 9 12 13.5 16.5 9" />
      </g>
    ) : (
      <g>
        <line x1="12" y1="3" x2="12" y2="13.5" />
        <polyline points="7.5 9 12 13.5 16.5 9" />
      </g>
    )}
  </svg>
);

export const WifiConnectingIcon = ({ animated = true, className = 'h-[18px] w-[18px]' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path className={animated ? 'animate-wifi-dot' : ''} d="M12 20h.01" />
    <path className={animated ? 'animate-wifi-1' : ''} d="M8.5 16.43a5 5 0 0 1 7 0" />
    <path className={animated ? 'animate-wifi-2' : ''} d="M5 12.86a10 10 0 0 1 14 0" />
    <path className={animated ? 'animate-wifi-3' : ''} d="M2 8.82a15 15 0 0 1 20 0" />
  </svg>
);

export const WifiConnectedIcon = ({ className = 'h-[18px] w-[18px]' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h.01" />
    <path d="M2 8.82a15 15 0 0 1 20 0" />
    <path d="M5 12.86a10 10 0 0 1 14 0" />
    <path d="M8.5 16.43a5 5 0 0 1 7 0" />
    <circle cx="18" cy="18" r="4.5" fill="#10b981" stroke="#ffffff" strokeWidth="1.2" />
    <polyline points="16.2 18 17.4 19.2 20 16.6" stroke="#ffffff" strokeWidth="1.5" />
  </svg>
);

const getStatus = ({ networkStatus, progress, busy, snapshot }) => {
  if (networkStatus === 'offline') return { tone: 'red', icon: WifiOff, title: 'Нет интернета', description: snapshot ? 'Сохранённые данные доступны в офлайн-режиме.' : 'Подключитесь к интернету для загрузки данных.' };
  if (networkStatus === 'server_unavailable') return { tone: 'amber', icon: WifiConnectingIcon, title: 'Сервер недоступен', description: 'Проверяем соединение. Сохранённые данные доступны на устройстве.' };
  if (networkStatus === 'restored') return { tone: 'green', icon: WifiConnectedIcon, title: 'Связь восстановлена', description: 'Сервер снова доступен.' };
  if (busy) return { tone: 'blue', icon: CloudDownload, title: 'Подготовка к офлайн-режиму', description: 'Можно продолжать пользоваться приложением.' };
  if (progress.phase === 'error') return { tone: 'amber', icon: AlertTriangle, title: 'Загрузка не завершена', description: progress.error };
  if (progress.phase === 'paused') return { tone: 'gray', icon: Pause, title: 'Загрузка приостановлена', description: 'Продолжите, когда будет удобно.' };
  if (snapshot?.schemaVersion === 1) return { tone: 'green', icon: ShieldCheck, title: 'Офлайн-режим готов', description: 'Данные сохранены на устройстве в режиме просмотра.' };
  return { tone: 'gray', icon: CloudDownload, title: 'Подготовьте данные для офлайн-режима', description: 'Сохраните нужные заказы, поручения и расходы на этом устройстве.' };
};

const COMPLETED_SHOW_DURATION_MS = 3000;
const ERROR_SHOW_DURATION_MS = 6000;

export const OfflineIndicator = () => {
  const offline = useOffline();
  const { animationsEnabled } = useInteractionPreferences();
  const [busyVisible, setBusyVisible] = useState(false);
  const [completedVisible, setCompletedVisible] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);

  const [restoredVisible, setRestoredVisible] = useState(false);
  const [restoredFadingOut, setRestoredFadingOut] = useState(false);
  const [errorVisible, setErrorVisible] = useState(false);
  const [errorFadingOut, setErrorFadingOut] = useState(false);

  const busy = Boolean(offline?.busy);
  const progress = offline?.progress;
  const phase = progress?.phase;
  const network = offline?.networkStatus;
  const enabled = Boolean(offline?.preferences?.enabled);
  const hasSnapshot = Boolean(offline?.snapshot?.schemaVersion === 1);

  const hadActiveDownloadRef = useRef(false);
  const hideTimerRef = useRef(null);
  const fadeTimerRef = useRef(null);
  const restoredHideTimerRef = useRef(null);
  const restoredFadeTimerRef = useRef(null);
  const errorHideTimerRef = useRef(null);
  const errorFadeTimerRef = useRef(null);

  const triggerCompletedBanner = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);

    setBusyVisible(false);
    setFadingOut(false);
    setCompletedVisible(true);

    fadeTimerRef.current = setTimeout(() => {
      setFadingOut(true);
    }, Math.max(0, COMPLETED_SHOW_DURATION_MS - 300));

    hideTimerRef.current = setTimeout(() => {
      setCompletedVisible(false);
      setFadingOut(false);
    }, COMPLETED_SHOW_DURATION_MS);
  };

  useEffect(() => {
    if (busy) {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
      setCompletedVisible(false);
      setFadingOut(false);

      const timer = setTimeout(() => {
        setBusyVisible(true);
        hadActiveDownloadRef.current = true;
      }, 200);
      return () => clearTimeout(timer);
    }

    setBusyVisible(false);
    return undefined;
  }, [busy]);

  useEffect(() => {
    if (!busy && hadActiveDownloadRef.current) {
      hadActiveDownloadRef.current = false;
      const isNetworkDown = ['offline', 'server_unavailable'].includes(network);
      const needsAttention = phase === 'error';
      if (!isNetworkDown && !needsAttention && hasSnapshot && phase === 'done') {
        triggerCompletedBanner();
      }
    }
  }, [busy, phase, network, hasSnapshot]);

  useEffect(() => {
    const onDataReady = () => {
      const isNetworkDown = ['offline', 'server_unavailable'].includes(network);
      const needsAttention = phase === 'error';
      if (!isNetworkDown && !needsAttention && enabled) {
        triggerCompletedBanner();
      }
    };
    window.addEventListener('offline-data-ready', onDataReady);
    return () => {
      window.removeEventListener('offline-data-ready', onDataReady);
    };
  }, [network, phase, enabled]);

  // Восстановление связи: показываем зеленый Wi-Fi с галочкой на 3 секунды
  useEffect(() => {
    if (network === 'restored') {
      if (restoredHideTimerRef.current) clearTimeout(restoredHideTimerRef.current);
      if (restoredFadeTimerRef.current) clearTimeout(restoredFadeTimerRef.current);

      setRestoredFadingOut(false);
      setRestoredVisible(true);

      restoredFadeTimerRef.current = setTimeout(() => {
        setRestoredFadingOut(true);
      }, Math.max(0, COMPLETED_SHOW_DURATION_MS - 300));

      restoredHideTimerRef.current = setTimeout(() => {
        setRestoredVisible(false);
        setRestoredFadingOut(false);
      }, COMPLETED_SHOW_DURATION_MS);
    }
  }, [network]);

  useEffect(() => {
    if (phase === 'error') {
      if (errorHideTimerRef.current) clearTimeout(errorHideTimerRef.current);
      if (errorFadeTimerRef.current) clearTimeout(errorFadeTimerRef.current);

      setErrorFadingOut(false);
      setErrorVisible(true);

      errorFadeTimerRef.current = setTimeout(() => {
        setErrorFadingOut(true);
      }, Math.max(0, ERROR_SHOW_DURATION_MS - 400));

      errorHideTimerRef.current = setTimeout(() => {
        setErrorVisible(false);
        setErrorFadingOut(false);
      }, ERROR_SHOW_DURATION_MS);
    } else {
      setErrorVisible(false);
      setErrorFadingOut(false);
    }
  }, [phase]);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
      if (restoredHideTimerRef.current) clearTimeout(restoredHideTimerRef.current);
      if (restoredFadeTimerRef.current) clearTimeout(restoredFadeTimerRef.current);
      if (errorHideTimerRef.current) clearTimeout(errorHideTimerRef.current);
      if (errorFadeTimerRef.current) clearTimeout(errorFadeTimerRef.current);
    };
  }, []);

  if (!offline) return null;

  const isOfflineNoInternet = network === 'offline';
  const isConnecting = network === 'server_unavailable';
  const isRestored = restoredVisible && !isOfflineNoInternet && !isConnecting;
  const needsAttention = errorVisible && phase === 'error' && !isOfflineNoInternet && !isConnecting;
  const isDownloading = busyVisible && !isOfflineNoInternet && !isConnecting;
  const isCompleted = completedVisible && !isDownloading && !isOfflineNoInternet && !isConnecting && !needsAttention;

  if (!enabled && !isOfflineNoInternet && !isConnecting) {
    return null;
  }

  // Показываем только в активных состояниях:
  // 1) Нет интернета (красный WifiOff)
  // 2) Попытка соединения (оранжевый анимированный Wi-Fi)
  // 3) Связь восстановилась (зеленый Wi-Fi с галочкой, 3 сек)
  // 4) Ошибка скачивания (желтый AlertTriangle)
  // 5) Идет загрузка (синяя стрелка Telegram)
  // 6) Скачивание завершено (зеленая галочка Telegram, 3 сек)
  if (!isOfflineNoInternet && !isConnecting && !isRestored && !needsAttention && !isDownloading && !isCompleted) {
    return null;
  }

  let title = 'Офлайн-режим';
  let iconColor = 'text-gray-500 dark:text-gray-400';
  let IconComponent = null;

  if (isOfflineNoInternet) {
    title = 'Нет интернета. Офлайн-режим активен';
    iconColor = 'text-red-500 dark:text-red-400';
    IconComponent = <WifiOff className="h-[18px] w-[18px]" />;
  } else if (isConnecting) {
    title = 'Сервер недоступен. Проверяем связь... Офлайн-режим активен';
    iconColor = 'text-amber-500 dark:text-amber-400';
    IconComponent = <WifiConnectingIcon animated={animationsEnabled} className="h-[18px] w-[18px]" />;
  } else if (isRestored) {
    title = 'Связь с сервером восстановлена';
    iconColor = 'text-emerald-500 dark:text-emerald-400';
    IconComponent = <WifiConnectedIcon className="h-[18px] w-[18px]" />;
  } else if (needsAttention) {
    title = progress.error || 'Ошибка загрузки офлайн-данных — подробнее';
    iconColor = 'text-amber-500 dark:text-amber-400';
    IconComponent = <AlertTriangle className="h-[18px] w-[18px]" />;
  } else if (isDownloading) {
    title = 'Скачивание данных для офлайн-режима...';
    iconColor = 'text-blue-500 dark:text-blue-400';
    IconComponent = (
      <TelegramDownloadIcon
        downloading={true}
        completed={false}
        animated={animationsEnabled}
        className="h-[18px] w-[18px]"
      />
    );
  } else if (isCompleted) {
    title = 'Офлайн-режим готов: все данные сохранены';
    iconColor = 'text-emerald-500 dark:text-emerald-400';
    IconComponent = (
      <TelegramDownloadIcon
        downloading={false}
        completed={true}
        animated={animationsEnabled}
        className="h-[18px] w-[18px]"
      />
    );
  }

  const isFading = (isCompleted && fadingOut) || (isRestored && restoredFadingOut) || (needsAttention && errorFadingOut);

  return (
    <button
      type="button"
      onClick={() => { haptic('selection'); offline.openModal('status'); }}
      className={`relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-800 ${iconColor} ${isFading ? 'opacity-0 scale-90' : 'opacity-100 scale-100'}`}
      aria-label={title}
      title={title}
    >
      {IconComponent}
    </button>
  );
};

export const OfflineSettingsControl = () => {
  const offline = useOffline();
  if (!offline) return null;
  const enabled = offline.preferences.enabled;
  const toggleOffline = () => { haptic('selection'); offline.updatePreferences({ enabled: !enabled }); };
  return <div className={`flex items-center rounded-xl border transition-colors ${enabled ? 'border-blue-500 bg-blue-50 shadow-[0_6px_18px_rgba(59,130,246,0.12)] dark:bg-blue-950/20' : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'}`}>
    <button type="button" aria-label="Переключить офлайн-режим"
      onClick={toggleOffline}
      className="flex min-w-0 flex-1 items-center gap-3 rounded-xl px-3.5 py-2.5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500">
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${enabled ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'}`}><CloudDownload className="h-[18px] w-[18px]" /></span>
      <span className="min-w-0 flex-1 text-sm font-semibold leading-snug text-gray-900 dark:text-gray-100">Офлайн-режим</span>
    </button>
    <button type="button" disabled={!enabled} aria-disabled={!enabled} onClick={() => { haptic('selection'); offline.openModal('settings'); }} aria-label="Настройки офлайн-режима" title={enabled ? 'Настройки офлайн-режима' : 'Сначала включите офлайн-режим'}
      className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${enabled ? 'text-blue-600 hover:bg-white/80 hover:text-blue-700 dark:text-blue-400 dark:hover:bg-gray-700' : 'text-gray-400 dark:text-gray-500'}`}>
      <Settings2 className="h-[18px] w-[18px]" />
    </button>
    <button type="button" role="switch" aria-checked={enabled} aria-label="Офлайн-режим" onClick={toggleOffline}
      className="mr-3 inline-flex shrink-0 items-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500">
      <span aria-hidden="true" className="toggle-switch" style={{ '--toggle-bg': enabled ? 'rgb(59 130 246)' : 'rgb(156 163 175)', '--toggle-height': '26px', '--toggle-width': '44px', '--knob-x': enabled ? '20px' : '2px' }}><span className="toggle-switch-knob" /></span>
    </button>
  </div>;
};

const DownloadProgress = ({ progress, busy, animationsEnabled }) => {
  const steps = [
    { id: 'shell', label: 'Файлы приложения', icon: Smartphone },
    { id: 'data', label: 'Заказы, поручения и расходы', icon: Package },
    { id: 'saving', label: 'Сохранение на устройстве', icon: Database },
  ];
  const index = progress.phase === 'done' ? 3 : steps.findIndex(step => step.id === progress.phase);
  const fraction = progress.phase === 'shell' && progress.shellTotal ? progress.shellCompleted / progress.shellTotal
    : progress.phase === 'data' && progress.totalBytes ? Math.min(1, progress.bytes / progress.totalBytes) : 0;
  const percent = Math.round(((Math.max(0, index) + fraction) / 3) * 100);
  const indeterminate = busy && ((index === 0 && !progress.shellTotal) || (index === 1 && !progress.totalBytes) || index === 2);
  const detail = progress.phase === 'shell' ? (progress.shellTotal ? `${progress.shellCompleted} из ${progress.shellTotal} файлов` : 'Проверяем файлы для открытия без сети')
    : progress.phase === 'data' ? (progress.bytes ? `${formatBytes(progress.bytes)}${progress.totalBytes ? ` из ${formatBytes(progress.totalBytes)}` : ' загружено'}` : 'Собираем данные по вашим настройкам')
      : progress.phase === 'saving' ? 'Проверяем и сохраняем весь пакет' : 'Все этапы завершены';
  return <div className="rounded-2xl border border-gray-200 p-4 dark:border-gray-700">
    <div className="mb-2 flex items-center justify-between gap-3 text-xs font-medium text-gray-500 dark:text-gray-400"><span>{detail}</span><span className="shrink-0 tabular-nums">{index === 3 ? 'Готово' : `${Math.max(0, index)} / 3 этапа`}</span></div>
    <div role="progressbar" aria-label="Подготовка офлайн-режима" aria-valuemin={0} aria-valuemax={100}
      aria-valuenow={indeterminate ? undefined : percent} aria-valuetext={detail}
      className="relative h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
      <div className={`h-full rounded-full ${index === 3 ? 'bg-emerald-500' : 'bg-blue-500'} ${animationsEnabled ? 'transition-[width] duration-300' : ''}`} style={{ width: `${percent}%` }} />
      {indeterminate && <div className={`absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-blue-400/30 to-transparent ${animationsEnabled ? 'animate-pulse' : ''}`} />}
    </div>
    <div className="mt-4 space-y-3">{steps.map((step, stepIndex) => {
      const done = index > stepIndex;
      const active = busy && index === stepIndex;
      const Icon = done ? CheckCircle2 : active ? Loader2 : step.icon;
      return <div key={step.id} className={`flex items-center gap-2.5 text-sm ${done ? 'text-emerald-600 dark:text-emerald-400' : active ? 'font-medium text-blue-700 dark:text-blue-300' : 'text-gray-400 dark:text-gray-500'}`}>
        <Icon className={`h-4 w-4 shrink-0 ${active && animationsEnabled ? 'animate-spin' : ''}`} /><span>{step.label}</span>
      </div>;
    })}</div>
  </div>;
};

export const OfflineModal = () => {
  const offline = useOffline();
  const { animationsEnabled } = useInteractionPreferences();
  const [draft, setDraft] = useState({ days: 7, scope: 'mine' });
  const [confirmClear, setConfirmClear] = useState(false);
  const [progressVisible, setProgressVisible] = useState(false);
  const panelRef = useRef(null);
  const open = Boolean(offline?.modal);
  const view = offline?.modal;
  const { preferences, snapshot, progress, busy, networkStatus } = offline || {
    preferences: { days: 7, scope: 'mine', enabled: false },
    snapshot: null,
    progress: { phase: 'idle' },
    busy: false,
    networkStatus: 'unknown',
  };
  useModalBackButton(open, () => offline?.closeModal());
  useEffect(() => {
    if (open) { setDraft(offline.preferences); setConfirmClear(false); }
  }, [open, view, offline?.preferences]);
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const frame = requestAnimationFrame(() => panelRef.current?.querySelector('button')?.focus());
    const keyDown = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); offline.closeModal(); }
      if (event.key !== 'Tab') return;
      const buttons = panelRef.current?.querySelectorAll('button:not(:disabled), [tabindex="0"]');
      if (!buttons?.length) return;
      const first = buttons[0]; const last = buttons[buttons.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', keyDown);
    return () => { cancelAnimationFrame(frame); document.removeEventListener('keydown', keyDown); previous?.focus?.(); };
  // Focus should remain stable while progress updates.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (busy) {
      setProgressVisible(true);
      return undefined;
    }
    if (progress.phase !== 'done') {
      setProgressVisible(false);
      return undefined;
    }
    setProgressVisible(true);
    const timer = setTimeout(() => setProgressVisible(false), PROGRESS_RESULT_MIN_MS);
    return () => clearTimeout(timer);
  }, [busy, progress.phase]);
  if (!offline) return null;
  const isAdminPlus = Boolean(
    offline?.isAdminPlus
    || ['admin', 'developer'].includes(String(offline?.userType || snapshot?.userType || localStorage.getItem('user_type') || '').trim().toLowerCase())
  );
  const status = getStatus(offline);
  const StatusIcon = status.icon;
  const blocked = ['offline', 'server_unavailable'].includes(networkStatus);
  const settings = view === 'settings';
  const changed = draft.days !== preferences.days || draft.scope !== preferences.scope;
  const syncedAt = formatOfflineCacheDateTime(snapshot?.syncedAt);
  const scopeOptions = [
    { value: 'mine', icon: UserRound, title: 'Только мои', description: 'Свои заказы, поручения и расходы' },
    { value: 'available', icon: Users, title: 'Мои и доступные', description: 'Свои данные + задания, доступные для работы' },
    ...(isAdminPlus ? [
      { value: 'all', icon: Layers, title: 'Все', description: 'Заказы, поручения всех сотрудников, компания и расчётник' }
    ] : [])
  ];
  const expenseCount = snapshot?.expensesData?.total_transactions
    ?? snapshot?.expensesData?.myExpenses?.transactions?.length
    ?? 0;

  return <ModalShell open={open} onClose={offline.closeModal} variant={settings ? 'window' : 'sheet'} swipeToClose={true} panelClassName={`flex ${settings ? 'max-h-[85vh] !p-0' : 'max-h-[90vh]'} max-w-md flex-col overflow-hidden`} panelProps={{ role: 'dialog', 'aria-modal': true, 'aria-labelledby': 'offline-modal-title' }}>
    <div ref={panelRef} className="flex min-h-0 flex-col">
      <WindowModalHeader icon={settings ? Settings2 : CloudDownload} title={settings ? 'Настройки офлайн-режима' : 'Офлайн-режим'} titleId="offline-modal-title" onClose={offline.closeModal} />
      <div className="min-h-0 space-y-5 overflow-y-auto overscroll-contain p-5">
        {settings ? <>
          <p className="text-sm leading-relaxed text-gray-500 dark:text-gray-400">Выберите, какие данные сохранять на этом устройстве для работы без интернета.</p>
          <fieldset><legend className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">На сколько дней вперёд</legend>
            <div className="grid grid-cols-4 gap-2">{OFFLINE_DAY_OPTIONS.map(days => <button key={days} type="button" aria-pressed={draft.days === days} onClick={() => { haptic('selection'); setDraft(previous => ({ ...previous, days })); }}
              className={`min-h-11 rounded-xl border px-1 text-sm font-semibold transition-colors ${draft.days === days ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300' : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800'}`}>{dayLabel(days)}</button>)}</div>
            <p className="mt-2.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">Включая сегодня. Свои просроченные задания и задания без даты тоже сохраняются.</p>
          </fieldset>
          <fieldset><legend className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Какие данные скачивать</legend>
            <div className="space-y-2">{scopeOptions.map(option => <button key={option.value} type="button" aria-pressed={draft.scope === option.value} onClick={() => { haptic('selection'); setDraft(previous => ({ ...previous, scope: option.value })); }}
              className={`flex w-full items-center gap-3 rounded-xl border p-3.5 text-left transition-colors ${draft.scope === option.value ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30' : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800'}`}>
              <option.icon className={`h-5 w-5 shrink-0 ${draft.scope === option.value ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
              <span className="min-w-0 flex-1"><span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">{option.title}</span><span className="mt-1 block text-xs leading-relaxed text-gray-500 dark:text-gray-400">{option.description}</span></span>
              <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${draft.scope === option.value ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 dark:border-gray-600'}`}>{draft.scope === option.value && <Check className="h-3 w-3" />}</span>
            </button>)}</div>
          </fieldset>
          <div className="rounded-xl bg-gray-50 p-3 text-xs leading-relaxed text-gray-500 dark:bg-gray-800/60 dark:text-gray-400">{preferences.enabled ? 'Данные обновляются автоматически, пока вы пользуетесь приложением.' : 'Автоматическая загрузка выключена. Сохранённые данные остаются доступны; пакет можно обновить вручную.'} Заказы, поручения и расходы сохраняются по выбранному составу.</div>
          <button type="button" onClick={() => offline.openModal('status')} className="flex w-full items-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400"><HardDrive className="h-4 w-4" /><span className="flex-1 text-left">Сохранённые данные и загрузка</span><ChevronRight className="h-4 w-4" /></button>
        </> : <>
          <div className={`flex items-start gap-3 rounded-2xl p-4 ${tones[status.tone]}`} role="status">
            <StatusIcon className="mt-0.5 h-6 w-6 shrink-0" /><div><div className="text-sm font-semibold leading-snug">{status.title}</div><p className="mt-1 text-xs leading-relaxed opacity-90">{status.description}</p></div>
          </div>
          {progressVisible && <DownloadProgress progress={progress} busy={busy} animationsEnabled={animationsEnabled} />}
          <div className="space-y-3">
            <div className="flex items-center justify-between"><span className="text-xs font-medium uppercase tracking-wider text-gray-400">На устройстве</span><button type="button" onClick={() => offline.openModal('settings')} className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"><Settings2 className="h-3.5 w-3.5" />Настроить</button></div>
            <div className="grid grid-cols-3 gap-2">{[
              { label: 'Заказов', value: snapshot?.orderDetails?.length || 0, icon: Package },
              { label: 'Поручений', value: snapshot?.taskDetails?.length || 0, icon: CheckCircle2 },
              { label: 'Расходов', value: expenseCount, icon: WalletCards },
            ].map(item => <div key={item.label} className="rounded-xl border border-gray-200 p-3 dark:border-gray-700"><div className="flex items-center justify-between text-gray-400"><span className="text-xs">{item.label}</span><item.icon className="h-4 w-4" /></div><div className="mt-1 text-xl font-semibold tabular-nums text-gray-900 dark:text-white">{item.value}</div></div>)}</div>
            <dl className="space-y-2 text-xs"><div className="flex justify-between gap-3"><dt className="text-gray-500 dark:text-gray-400">Последнее сохранение</dt><dd className="text-right font-medium text-gray-700 dark:text-gray-200">{syncedAt || 'Ещё не загружено'}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-gray-500 dark:text-gray-400">Состав пакета</dt><dd className="text-right font-medium text-gray-700 dark:text-gray-200">{dayLabel(snapshot?.preferences?.days || preferences.days)} · {(snapshot?.preferences || preferences).scope === 'all' ? 'Все данные' : (snapshot?.preferences || preferences).scope === 'available' ? 'Мои и доступные' : 'Только мои'}</dd></div>
              {snapshot?.bytes > 0 && <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">Данные на устройстве</dt><dd className="font-medium text-gray-700 dark:text-gray-200">{formatBytes(snapshot.bytes)}</dd></div>}
            </dl>
            {busy && snapshot && <p className="text-xs leading-relaxed text-gray-400">Пока идёт загрузка, доступна предыдущая сохранённая копия.</p>}
          </div>
          {snapshot && !busy && <div className="border-t border-gray-100 pt-3 dark:border-gray-800">{confirmClear ? <div className="space-y-3"><p className="text-xs leading-relaxed text-gray-500 dark:text-gray-400">Удалить сохранённые данные с этого устройства? Для повторного скачивания понадобится интернет.</p><div className="flex gap-2"><button type="button" onClick={() => { offline.clearSaved(); setConfirmClear(false); }} className="rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 dark:bg-red-950/30 dark:text-red-300">Удалить</button><button type="button" onClick={() => setConfirmClear(false)} className="px-3 py-2 text-xs text-gray-500">Отмена</button></div></div>
            : <button type="button" onClick={() => setConfirmClear(true)} className="inline-flex items-center gap-2 py-1 text-xs text-gray-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" />Удалить сохранённые данные</button>}</div>}
        </>}
      </div>
      <div className="flex shrink-0 gap-2 border-t border-gray-100 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] dark:border-gray-800">
        {settings ? <><button type="button" onClick={offline.closeModal} className={`${secondaryButton} flex-1`}>Закрыть</button><button type="button" disabled={!changed} onClick={() => { haptic('success'); offline.updatePreferences({ days: draft.days, scope: draft.scope }); offline.openModal('status'); }} className={`${primaryButton} flex-1`}>Сохранить</button></>
          : busy ? <button type="button" onClick={offline.cancel} className={`${secondaryButton} w-full`}><Pause className="h-4 w-4" />Приостановить</button>
            : <button type="button" disabled={blocked} onClick={() => { haptic('selection'); offline.refresh(); }} className={`${primaryButton} w-full`}><RefreshCw className="h-4 w-4" />{progress.phase === 'paused' ? 'Продолжить загрузку' : snapshot ? 'Обновить сейчас' : 'Скачать данные'}</button>}
      </div>
    </div>
  </ModalShell>;
};
